import React from 'react';
import {type MonthCaptionProps, useDayPicker} from 'react-day-picker';

import ChevronLeftIcon from '@/components/Icons/ChevronLeftIcon';
import ChevronRightIcon from '@/components/Icons/ChevronRightIcon';
import {twx} from '@/utils/tailwindUtils';

type CalendarHeaderProps = MonthCaptionProps & {
    formatHeaderLabel: (date: Date) => string;
    ariaLabelPrevMonth: string;
    ariaLabelNextMonth: string;
};

const CalendarHeader = ({
    calendarMonth,
    formatHeaderLabel,
    ariaLabelPrevMonth,
    ariaLabelNextMonth,
}: CalendarHeaderProps) => {
    const {goToMonth, nextMonth, previousMonth} = useDayPicker();
    const displayMonth = calendarMonth.date;
    const handlePrevious = () => {
        if (previousMonth) {
            goToMonth(previousMonth);
        }
    };

    const handleNext = () => {
        if (nextMonth) {
            goToMonth(nextMonth);
        }
    };

    const isAtMinMonth = !previousMonth;
    const isAtMaxMonth = !nextMonth;

    return (
        <div className='flex items-center justify-between'>
            <HeaderButton
                onClick={handlePrevious}
                disabled={isAtMinMonth}
                ariaLabel={ariaLabelPrevMonth}
                icon={ChevronLeftIcon}
            />
            <h2 className='leading-24 m-0 text-16 font-bold text-black'>{formatHeaderLabel(displayMonth)}</h2>
            <HeaderButton
                onClick={handleNext}
                disabled={isAtMaxMonth}
                ariaLabel={ariaLabelNextMonth}
                icon={ChevronRightIcon}
            />
        </div>
    );
};

export default CalendarHeader;

type HeaderButtonProps = {
    onClick: () => void;
    disabled: boolean;
    ariaLabel: string;
    icon: React.ComponentType<{className?: string}>;
};

const HeaderButton = ({onClick, disabled, ariaLabel, icon: Icon}: HeaderButtonProps) => {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            aria-label={ariaLabel}
            className={twx(
                'flex size-8 cursor-pointer items-center justify-center rounded-full border border-primary p-2',
                disabled && 'cursor-default opacity-30'
            )}
            type='button'
        >
            <Icon className='size-4' />
        </button>
    );
};
